import type { IUser } from "@/domains/shared/models/IUser";
import {
    canAccessTherapistFinanceNav,
    CLINIC_HOME_PATH,
    CLINIC_SUBSCRIPTION_PATH,
    hasClinicMembership,
    shouldShowSoloBillingInProfissionalNav,
} from "@/domains/multi/clinicMemberPaths";
import { THERAPIST_NAV_TRANSLATIONS } from "@/domains/shared/navigation/constants";
import { isFreePlan } from "@/domains/subscription/plans";
import { resolveSecretaryModuleActive, resolveTeamChatActive } from "@/domains/shared/services/secretaryClinicalPaths";
import { getTranslation } from "@mivo/i18n";
import type { LucideIcon } from "lucide-react";
import {
    Building2,
    CalendarDays,
    DollarSign,
    HelpCircle,
    LayoutDashboard,
    MessageCircle,
    Settings,
    Shield,
    Store,
    User,
    Users,
    UsersRound,
    Wallet,
} from "lucide-react";

export type TherapistNavItem = {
    to: string;
    label: string;
    icon: LucideIcon;
    end?: boolean;
};

const SECRETARY_MODULE_ROUTES = new Set([
    "/profissional/administrativo",
    "/profissional/chat-equipe",
]);

/** Solo billing/marketplace — hidden for any active clinic membership (**BR-35**). */
const CLINIC_MEMBER_SOLO_BILLING_ROUTES = new Set([
    CLINIC_SUBSCRIPTION_PATH,
    "/profissional/marketplace",
]);

/** Not included on Gratuito — hidden from nav when subscription is the free tier. */
const FREE_PLAN_HIDDEN_ROUTES = new Set([
    "/profissional/financeiro",
    "/profissional/chat",
]);

function therapistNavBase(): TherapistNavItem[] {
    return [
        { to: "/profissional", label: getTranslation(THERAPIST_NAV_TRANSLATIONS.home), icon: LayoutDashboard, end: true },
        { to: "/profissional/agenda", label: getTranslation(THERAPIST_NAV_TRANSLATIONS.agenda), icon: CalendarDays },
        { to: "/profissional/pacientes", label: getTranslation(THERAPIST_NAV_TRANSLATIONS.patients), icon: Users },
        { to: "/profissional/financeiro", label: getTranslation(THERAPIST_NAV_TRANSLATIONS.finance), icon: Wallet },
        { to: "/profissional/administrativo", label: getTranslation(THERAPIST_NAV_TRANSLATIONS.secretaryTeam), icon: UsersRound },
        { to: "/profissional/solicitacoes", label: getTranslation(THERAPIST_NAV_TRANSLATIONS.requests), icon: HelpCircle },
        { to: "/profissional/chat", label: getTranslation(THERAPIST_NAV_TRANSLATIONS.patientChat), icon: MessageCircle },
        { to: "/profissional/chat-equipe", label: getTranslation(THERAPIST_NAV_TRANSLATIONS.teamChat), icon: UsersRound },
        { to: "/profissional/configuracoes", label: getTranslation(THERAPIST_NAV_TRANSLATIONS.settings), icon: Settings },
        { to: "/profissional/privacidade", label: getTranslation(THERAPIST_NAV_TRANSLATIONS.privacy), icon: Shield },
        { to: "/profissional/marketplace", label: getTranslation(THERAPIST_NAV_TRANSLATIONS.marketplace), icon: Store },
        { to: CLINIC_SUBSCRIPTION_PATH, label: getTranslation(THERAPIST_NAV_TRANSLATIONS.subscription), icon: DollarSign },
        { to: "/profissional/perfil", label: getTranslation(THERAPIST_NAV_TRANSLATIONS.profile), icon: User },
    ];
}

/** Sidebar footer link (above profile) — not part of scrollable therapist nav. */
export function therapistClinicManagementNavItem(): TherapistNavItem {
    return {
        to: CLINIC_HOME_PATH,
        label: getTranslation(THERAPIST_NAV_TRANSLATIONS.clinicManagement),
        icon: Building2,
    };
}

export type TherapistNavUserContext = Pick<
    IUser,
    "modules" | "activeClinicId" | "clinic" | "subscriptionPlan" | "clinicRoles" | "clinicMemberships"
>;

export type BuildTherapistNavOptions = {
    /** When set, hides Assinatura/Marketplace from nav Profissional (**BR-35**). */
    activeClinicId?: string | null;
};

export function buildTherapistNav(
    userContext?: TherapistNavUserContext | null,
    options?: BuildTherapistNavOptions,
): TherapistNavItem[] {
    let base = resolveTeamChatActive(userContext as IUser | null | undefined)
        ? [...therapistNavBase()]
        : therapistNavBase().filter((item) => !SECRETARY_MODULE_ROUTES.has(item.to));

    const activeClinicId = options?.activeClinicId ?? userContext?.activeClinicId;
    const clinicMember = hasClinicMembership(userContext as IUser | null | undefined) || Boolean(activeClinicId);

    if (!resolveSecretaryModuleActive(userContext) || clinicMember) {
        base = base.filter((item) => item.to !== "/profissional/administrativo");
    }

    if (activeClinicId && !shouldShowSoloBillingInProfissionalNav(userContext as IUser | null | undefined)) {
        base = base.filter((item) => !CLINIC_MEMBER_SOLO_BILLING_ROUTES.has(item.to));
    }

    if (isFreePlan(userContext?.subscriptionPlan)) {
        base = base.filter((item) => !FREE_PLAN_HIDDEN_ROUTES.has(item.to));
    }

    if (!canAccessTherapistFinanceNav(userContext as IUser | null | undefined, { activeClinicId })) {
        base = base.filter((item) => item.to !== "/profissional/financeiro");
    }

    return base;
}

/** Fixed primary tabs on therapist mobile bottom bar (order matters). */
const THERAPIST_MOBILE_PRIMARY_ROUTES = [
    "/profissional",
    "/profissional/agenda",
    "/profissional/pacientes",
    "/profissional/chat",
    "/profissional/perfil",
] as const;

const THERAPIST_MOBILE_PRIMARY_ROUTE_SET = new Set<string>(THERAPIST_MOBILE_PRIMARY_ROUTES);

/** Therapist mobile footer: Início, Agenda, Pacientes, Chat, Perfil. */
export function buildTherapistMobileNav(
    userContext?: TherapistNavUserContext | null,
    options?: BuildTherapistNavOptions,
): TherapistNavItem[] {
    const nav = buildTherapistNav(userContext, options);
    const byRoute = new Map(nav.map((item) => [item.to, item]));

    return THERAPIST_MOBILE_PRIMARY_ROUTES.flatMap((to) => {
        const item = byRoute.get(to);
        if (!item) {
            return [];
        }
        if (to === "/profissional/chat") {
            return [{ ...item, label: getTranslation(THERAPIST_NAV_TRANSLATIONS.chatShort) }];
        }
        return [item];
    });
}

/** Remaining therapist nav items for the mobile “more” menu (excludes primary tabs). */
export function buildTherapistMobileMoreNav(
    userContext?: TherapistNavUserContext | null,
    options?: BuildTherapistNavOptions,
): TherapistNavItem[] {
    return buildTherapistNav(userContext, options).filter(
        (item) => !THERAPIST_MOBILE_PRIMARY_ROUTE_SET.has(item.to),
    );
}
